import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'gatsby';
import { device } from '../utils/breakpoints';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const HeroWrapper = styled.section`
  position: relative;
  background: linear-gradient(135deg, #0A1929 0%, #1A2942 50%, #0F2744 100%);
  padding: 120px 0 100px;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 50%, rgba(255, 215, 0, 0.15) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(124, 179, 66, 0.12) 0%, transparent 50%),
      radial-gradient(circle at 50% 20%, rgba(255, 107, 53, 0.1) 0%, transparent 50%);
    pointer-events: none;
    filter: blur(40px);
  }

  @media ${device.tablet} {
    padding: 80px 0 60px;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  animation: ${fadeIn} 0.8s ease-out;
`;

const HeroTitle = styled.h1`
  font-size: 44px;
  font-weight: 700;
  line-height: 1.2;
  color: #ffffff;
  margin-bottom: 24px;
  
  @media ${device.laptop} {
    font-size: 38px;
  }
  
  @media ${device.tablet} {
    font-size: 32px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 22px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 32px;
  max-width: 600px;
  
  @media ${device.tablet} {
    font-size: 18px;
  }
`;

const SupportingLine = styled.p`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 32px;
  font-style: italic;
  
  @media ${device.tablet} {
    font-size: 14px;
  }
`;

const CTAContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 48px;
`;

const PrimaryButton = styled(Link)`
  background: linear-gradient(135deg, #FFD700 0%, #FF6B35 100%);
  color: #0F2744;
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  display: inline-block;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(255, 107, 53, 0.4);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 215, 0, 0.5);
    background: linear-gradient(135deg, #FFE84D 0%, #FF8652 100%);
    color: #0F2744;
    text-decoration: none;
  }
`;

const SecondaryButton = styled(Link)`
  background: transparent;
  color: white;
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  display: inline-block;
  border: 2px solid rgba(124, 179, 66, 0.5);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(124, 179, 66, 0.15);
    border-color: #7CB342;
    color: white;
    text-decoration: none;
  }
`;

const TertiaryButton = styled(Link)`
  background: transparent;
  color: white;
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  display: inline-block;
  border: 2px solid rgba(255, 215, 0, 0.4);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 215, 0, 0.1);
    border-color: #FFD700;
    color: white;
    text-decoration: none;
  }
`;

const HeroVisual = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media ${device.tablet} {
    margin-top: 40px;
  }
`;

const VisualPlaceholder = styled.div`
  width: 100%;
  height: 400px;
  background: linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  
  @media ${device.tablet} {
    height: 300px;
  }
`;

const TrustBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  
  &::before {
    content: '✓';
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    background: rgba(76, 175, 80, 0.3);
    border-radius: 50%;
    color: #4CAF50;
    font-weight: bold;
  }
`;

const HeroSection = () => {
  return (
    <HeroWrapper>
      <Container>
        <Row className="align-items-center">
          <Col lg={6}>
            <HeroContent>
              <HeroTitle>
              Enterprise Control Plane for Operating Open-Source Platforms in Private Environments
              </HeroTitle>
              <HeroSubtitle>
                Accelerate innovation in private environments while maintaining data sovereignty, auditability, and organizational control.
              </HeroSubtitle>
              <SupportingLine>
                Designed for organizations that prioritize data privacy and security.
              </SupportingLine>
              <CTAContainer>
                <PrimaryButton to="/contact">
                  Request a Focused Demo
                </PrimaryButton>
                <SecondaryButton to="/solutions/catalogue">
                  Explore Use Cases
                </SecondaryButton>
              </CTAContainer>
              <TrustBadge>
                All solutions deployed in YOUR private environment • Complete data control
              </TrustBadge>
            </HeroContent>
          </Col>
          <Col lg={6}>
            <HeroVisual>
              <VisualPlaceholder>
                <svg viewBox="0 0 600 400" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
                  <defs>
                    <linearGradient id="heroYellow" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#FFD700', stopOpacity: 0.8 }} />
                      <stop offset="100%" style={{ stopColor: '#FF6B35', stopOpacity: 0.8 }} />
                    </linearGradient>
                    <linearGradient id="heroGreen" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#7CB342', stopOpacity: 0.8 }} />
                      <stop offset="100%" style={{ stopColor: '#5A9A2A', stopOpacity: 0.8 }} />
                    </linearGradient>
                    <linearGradient id="heroBlue" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#2196F3', stopOpacity: 0.9 }} />
                      <stop offset="100%" style={{ stopColor: '#1565C0', stopOpacity: 0.9 }} />
                    </linearGradient>
                    <linearGradient id="heroBluePlatform" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: 'rgba(33, 150, 243, 0.25)', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: 'rgba(21, 101, 192, 0.15)', stopOpacity: 1 }} />
                    </linearGradient>
                    <filter id="heroGlow">
                      <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>

                  {/* Background circles for depth - blue dominant */}
                  <circle cx="450" cy="100" r="100" fill="rgba(33, 150, 243, 0.12)"/>
                  <circle cx="150" cy="300" r="80" fill="rgba(21, 101, 192, 0.1)"/>
                  <circle cx="500" cy="320" r="70" fill="rgba(25, 118, 210, 0.08)"/>
                  <circle cx="300" cy="200" r="150" fill="rgba(30, 136, 229, 0.06)"/>

                  {/* Central Platform Core - Dominant Blue */}
                  <g transform="translate(200, 150)">
                    <rect width="200" height="100" rx="12" fill="url(#heroBlue)" stroke="rgba(33, 150, 243, 0.6)" strokeWidth="2" filter="url(#heroGlow)"/>
                    <text x="100" y="40" fontSize="18" fontWeight="700" fill="white" textAnchor="middle">
                      Ametnes
                    </text>
                    <text x="100" y="60" fontSize="14" fill="rgba(255,255,255,0.95)" textAnchor="middle">
                      Platform
                    </text>
                    <text x="100" y="82" fontSize="11" fill="rgba(255,215,0,0.95)" textAnchor="middle">
                      ⚡ 15min Setup
                    </text>
                  </g>

                  {/* Orbiting Solution Icons - Blue dominant */}
                  {/* AI Icon - Top Left */}
                  <g transform="translate(100, 80)" filter="url(#heroGlow)">
                    <circle cx="0" cy="0" r="35" fill="url(#heroBlue)"/>
                    <text x="0" y="8" fontSize="28" textAnchor="middle">🤖</text>
                  </g>

                  {/* Data Icon - Top Right */}
                  <g transform="translate(500, 80)" filter="url(#heroGlow)">
                    <circle cx="0" cy="0" r="35" fill="url(#heroBlue)"/>
                    <text x="0" y="8" fontSize="28" textAnchor="middle">🗄️</text>
                  </g>

                  {/* Business Icon - Bottom Left */}
                  <g transform="translate(100, 320)" filter="url(#heroGlow)">
                    <circle cx="0" cy="0" r="35" fill="url(#heroBlue)"/>
                    <text x="0" y="8" fontSize="28" textAnchor="middle">💼</text>
                  </g>

                  {/* Analytics Icon - Bottom Right */}
                  <g transform="translate(500, 320)" filter="url(#heroGlow)">
                    <circle cx="0" cy="0" r="35" fill="url(#heroBlue)"/>
                    <text x="0" y="8" fontSize="28" textAnchor="middle">📊</text>
                  </g>

                  {/* DevOps Icon - Middle Right - accent yellow */}
                  <g transform="translate(520, 200)" filter="url(#heroGlow)">
                    <circle cx="0" cy="0" r="30" fill="url(#heroYellow)"/>
                    <text x="0" y="6" fontSize="22" textAnchor="middle">🔧</text>
                  </g>

                  {/* Security Icon - Middle Left - accent yellow */}
                  <g transform="translate(80, 200)" filter="url(#heroGlow)">
                    <circle cx="0" cy="0" r="30" fill="url(#heroYellow)"/>
                    <text x="0" y="6" fontSize="22" textAnchor="middle">🔒</text>
                  </g>

                  {/* Connection lines from icons to platform - blue */}
                  <path d="M 100 115 L 230 180" stroke="rgba(33, 150, 243, 0.4)" strokeWidth="2" strokeDasharray="5,5"/>
                  <path d="M 500 115 L 370 180" stroke="rgba(33, 150, 243, 0.4)" strokeWidth="2" strokeDasharray="5,5"/>
                  <path d="M 100 285 L 230 220" stroke="rgba(33, 150, 243, 0.4)" strokeWidth="2" strokeDasharray="5,5"/>
                  <path d="M 500 285 L 370 220" stroke="rgba(33, 150, 243, 0.4)" strokeWidth="2" strokeDasharray="5,5"/>
                  <path d="M 490 200 L 400 200" stroke="rgba(33, 150, 243, 0.4)" strokeWidth="2" strokeDasharray="5,5"/>
                  <path d="M 110 200 L 200 200" stroke="rgba(33, 150, 243, 0.4)" strokeWidth="2" strokeDasharray="5,5"/>

                  {/* Infrastructure icons at bottom - blue themed */}
                  <g transform="translate(150, 360)">
                    <rect width="80" height="30" rx="6" fill="rgba(33, 150, 243, 0.15)" stroke="rgba(33, 150, 243, 0.4)" strokeWidth="1"/>
                    <text x="40" y="20" fontSize="11" fill="rgba(255,255,255,0.9)" textAnchor="middle">☁️ Cloud</text>
                  </g>
                  
                  <g transform="translate(260, 360)">
                    <rect width="80" height="30" rx="6" fill="rgba(33, 150, 243, 0.15)" stroke="rgba(33, 150, 243, 0.4)" strokeWidth="1"/>
                    <text x="40" y="20" fontSize="11" fill="rgba(255,255,255,0.9)" textAnchor="middle">🔐 Private</text>
                  </g>
                  
                  <g transform="translate(370, 360)">
                    <rect width="100" height="30" rx="6" fill="rgba(33, 150, 243, 0.15)" stroke="rgba(33, 150, 243, 0.4)" strokeWidth="1"/>
                    <text x="50" y="20" fontSize="11" fill="rgba(255,255,255,0.9)" textAnchor="middle">🏢 On-Premise</text>
                  </g>

                  {/* Floating badges - yellow accents */}
                  <g transform="translate(40, 30)">
                    <rect width="120" height="28" rx="14" fill="url(#heroYellow)"/>
                    <text x="60" y="18" fontSize="11" fontWeight="600" fill="#0F2744" textAnchor="middle">
                      60% Cost Savings
                    </text>
                  </g>

                  <g transform="translate(440, 30)">
                    <rect width="120" height="28" rx="14" fill="url(#heroYellow)"/>
                    <text x="60" y="18" fontSize="11" fontWeight="600" fill="#0F2744" textAnchor="middle">
                      200+ Solutions
                    </text>
                  </g>
                </svg>
              </VisualPlaceholder>
            </HeroVisual>
          </Col>
        </Row>
      </Container>
    </HeroWrapper>
  );
};

export default HeroSection;


